import intersection from 'lodash/intersection';
import { useAuthentication } from '@/composables/auth/useAuthentication';
import { useActiveCompany } from '@/composables/useActiveCompany';

export type PermissionsMap = Map<string, string[]>; // This assumes that the permissions are stored as a Map with the subject as key and an array of actions as value

export interface PermissionCheckOptions {
  subject: string | string[];
  action: string | string[];
  logic?: 'OR' | 'AND';
}

export const useAuthorization = () => {
  const { user } = useAuthentication();
  const { getActiveCompany } = useActiveCompany();

  /**
   * Checks if a user has permission to perform a specific action on a subject within a company.
   *
   * @param {string} companyCode - The company code.
   * @param {Map<string, PermissionsMap> | undefined} currentPermissions - The current permissions, stored in a map.
   * @param {PermissionCheckOptions} options - The options for permission check, including subject and action.
   * @returns {boolean} - Returns true if the user has permission, false otherwise.
   */
  const hasPermission = (
    companyCode: string,
    currentPermissions: Map<string, PermissionsMap> | undefined,
    { subject, action, logic = 'OR' }: PermissionCheckOptions,
  ): boolean => {
    if (!currentPermissions) {
      return false;
    }

    // check for company code
    const companyPermissions = typeof currentPermissions.get === 'function' && currentPermissions.get(companyCode);
    if (!companyPermissions) {
      return false;
    }

    const subjects = Array.isArray(subject) ? subject : [subject];
    const actions = Array.isArray(action) ? action : [action];

    if (logic === 'AND') {
      return subjects.every((s) => {
        const permissionSubject = companyPermissions.get(s);
        if (!permissionSubject) {
          return false;
        }
        return intersection(permissionSubject, actions).length > 0;
      });
    }

    return subjects.some((s) => {
      const permissionSubject = companyPermissions.get(s);
      if (!permissionSubject) {
        return false;
      }
      return intersection(permissionSubject, actions).length > 0;
    });
  };

  /**
   * Checks if the logged user has the specified permission.
   * @param {string|string[]} subject - The subject(s) of the permission.
   * @param {string|string[]} action - The action(s) of the permission.
   * @param {'OR'|'AND'} logic - The logic to use when multiple subjects are provided.
   * @returns {boolean} - true if the logged user has the specified permission, false otherwise.
   */
  const hasLoggedUserPermission = (
    subject: string | string[],
    action: string | string[],
    logic: 'OR' | 'AND' = 'OR',
  ): boolean => {
    const companyCode = getActiveCompany().code;
    return hasPermission(companyCode, user.value.mappedPermissions, { subject, action, logic });
  };

  return {
    hasPermission,
    hasLoggedUserPermission,
  };
};
